import * as React from "react";

import { DatasheetFieldComponentGetDto } from "@common/dto/datasheet/field-component/get-dto";
import { IDatasheetResultElement } from "@modules/datasheets/components/result/types/datasheet-element";
import { getCommonFieldBasedElementParams, useDatasheetElementController } from "@modules/datasheets/components/result/controllers/datasheet-element";
import { convertFromMMDDYYYY } from "@modules/datasheets/utils/date-format";
import { useMessage } from '@common/hooks/message';
import { TABLE_VALID_DATE_FORMATS, parseDateString, isValidDate, formatDateAsApi, parseApiDateString } from "@modules/datasheets/utils/date-validation";
import { InfoMessage } from "@common/components/info-message";

export type DateFieldProps = IDatasheetResultElement<DatasheetFieldComponentGetDto>;


export const useDateFieldController = (props: DateFieldProps) => {
  const { datasheetElement, onChange } = props;
  const { showMessage, closeMessage, messageProps } = useMessage();

  // Get the current value from the field - could be in various formats
  const rawValue = (datasheetElement.original as any).rawValue ?? '';
  const displayValue = (datasheetElement.original as any).displayValue ?? '';
  const fieldName = (datasheetElement.original.field as any).name;

  // Use field.name if explicitly set (including empty string), otherwise fall back to rawValue or displayValue
  // This ensures that when a date is deleted (empty string), it stays deleted
  const currentValue = fieldName !== undefined ? fieldName : (rawValue || displayValue);

  // Convert to Date object for DatePicker
  // DatePicker will handle displaying it in MMDDYYYY format via dateFormat prop
  const getDatePickerValue = React.useCallback(() => {
    if (!currentValue) {
      return null;
    }

    // If it's already in YYYY-MM-DD format (from API), parse it directly using local components
    const directParsed = parseApiDateString(currentValue);
    if (directParsed) {
      return directParsed;
    }

    // If it's already in MMDDYYYY format (8 digits), convert it
    if (/^\d{8}$/.test(currentValue)) {
      const yyyymmdd = convertFromMMDDYYYY(currentValue);
      return yyyymmdd ? parseApiDateString(yyyymmdd) : null;
    }

    // Otherwise try to parse as date string (MM/DD/YYYY format)
    const parsedDate = parseDateString(currentValue);
    if (parsedDate) {
      return parsedDate;
    }

    // Last resort: try convertFromMMDDYYYY and parse
    const yyyymmdd = convertFromMMDDYYYY(currentValue);
    return yyyymmdd ? parseApiDateString(yyyymmdd) : null;
  }, [currentValue, fieldName, rawValue, displayValue]);

  const [datePickerValue, setDatePickerValue] = React.useState<Date | null>(getDatePickerValue());
  // Store raw input value to capture it before react-datepicker clears it
  // Use null to distinguish between "never set" and "explicitly set to empty"
  const [rawInputValue, setRawInputValue] = React.useState<string | null>(null);
  // Track if date was changed via picker (not manual typing)
  const dateChangedViaPickerRef = React.useRef<boolean>(false);

  React.useEffect(() => {
    // If fieldName is explicitly empty string, clear the date picker
    if (fieldName === '') {
      setDatePickerValue(null);
      return;
    }
    const newDate = getDatePickerValue();
    if (newDate?.getTime() !== datePickerValue?.getTime()) {
      setDatePickerValue(newDate);
    }
  }, [currentValue, getDatePickerValue, datePickerValue, fieldName, rawValue, displayValue]);

  const {
    headingSizeDependencies,
    headingPlacedHorizontally,
    headingPosition
  } = getCommonFieldBasedElementParams(props);

  const baseDatasheetElementController = useDatasheetElementController(
    props,
    { headingSizeDependencies }
  );

  const handleDatePickerChange = React.useCallback((date: Date | null) => {
    setDatePickerValue(date);

    // If there's raw input in progress, treat this as part of manual typing
    // and DO NOT immediately persist via API. Manual input will be validated
    // and saved (or rejected) by handleRawInputChange/handleDateBlur instead.
    if (rawInputValue && rawInputValue.trim() !== '') {
      dateChangedViaPickerRef.current = false;
      return;
    }

    // No raw input: this is a true picker-driven change.
    dateChangedViaPickerRef.current = true;

    if (date) {
      // Save the value in YYYY-MM-DD format for API
      onChange(datasheetElement.id, {
        ...datasheetElement,
        original: {
          ...datasheetElement.original,
          field: {
            ...datasheetElement.original.field,
            name: formatDateAsApi(date),
          },
        },
      });
    } else {
      // User cleared the date via picker - save empty string immediately
      onChange(datasheetElement.id, {
        ...datasheetElement,
        original: {
          ...datasheetElement.original,
          field: {
            ...datasheetElement.original.field,
            name: '',
          },
        },
      });
    }
  }, [onChange, datasheetElement, rawInputValue]);

  // Handle raw input change (capture value before react-datepicker clears it)
  const handleRawInputChange = React.useCallback((value: string) => {
    // Always store the raw input value, including empty strings
    // This captures what the user actually typed, even if it's empty
    setRawInputValue(value);
    // Reset flag when user types manually (not via picker)
    dateChangedViaPickerRef.current = false;

    // Dynamically update datepicker preview if a valid format is entered,
    // but DO NOT persist to API here. Actual save happens on blur/Enter
    // via handleDateBlur after full validation.
    if (value && value.trim() !== '') {
      const parsedDate = parseDateString(value);
      if (parsedDate) {
        // Update datepicker value immediately when valid format is detected
        setDatePickerValue(parsedDate);
      } else {
        // Invalid format - clear datepicker but keep raw input for validation on blur
        setDatePickerValue(null);
      }
    } else {
      // Empty value - clear datepicker preview; save happens on blur
      setDatePickerValue(null);
    }
  }, []);

  // Validate date on blur and show error message (matching GWT behavior)
  const handleDateBlur = React.useCallback((value?: string) => {
    // If date was changed via picker (dropdown), handle it
    if (dateChangedViaPickerRef.current) {
      dateChangedViaPickerRef.current = false;
      setRawInputValue('');

      // The value was already saved by handleDatePickerChange (including empty if cleared)
      return;
    }

    // Prioritize rawInputValue if it was set (it captures what user actually typed)
    // Use null to distinguish between "never set" and "explicitly set to empty"
    // If rawInputValue is null, user didn't type anything, so use blur value
    const valueToValidate = rawInputValue !== null ? rawInputValue : (value || '');

    // Clear stored raw input after using it
    setRawInputValue(null);

    // Check for empty value - allow empty dates
    if (valueToValidate === '' || (valueToValidate && valueToValidate.trim() === '')) {
      // Empty string - clear the date field on blur and save empty value
      setDatePickerValue(null);

      onChange(datasheetElement.id, {
        ...datasheetElement,
        original: {
          ...datasheetElement.original,
          field: {
            ...datasheetElement.original.field,
            name: '',
          },
        },
      });
      return;
    }

    // Validate the date string
    if (!isValidDate(valueToValidate)) {
      // Show error message with accepted formats (matching AC)
      if (showMessage) {
        const content = React.createElement(
          InfoMessage,
          null,
          'Invalid date format. Dates must be entered as:',
          React.createElement(
            'ul',
            { style: { marginTop: 4, paddingLeft: 20 } },
            TABLE_VALID_DATE_FORMATS.map(format =>
              React.createElement('li', { key: format.value }, format.label)
            )
          )
        );


        showMessage({
          header: 'Information Message',
          content,
          size: 'small',
        });
      }

      // Restore the value the user actually saw before typing (local state),
      // This keeps an initially empty field empty on invalid input, and reverts
      // back to the last visible valid date when there was one.
      setDatePickerValue(datePickerValue);

      // Don't call onChange - retain the previous valid value
      return; // Don't proceed with valid date handling
    } else {
      // Valid date - parse and set it
      const parsedDate = parseDateString(valueToValidate);
      if (parsedDate) {
        setDatePickerValue(parsedDate);

        onChange(datasheetElement.id, {
          ...datasheetElement,
          original: {
            ...datasheetElement.original,
            field: {
              ...datasheetElement.original.field,
              name: formatDateAsApi(parsedDate),
            },
          },
        });
      }
    }
  }, [onChange, datasheetElement, showMessage, rawInputValue, datePickerValue]);

  return {
    datePickerValue,
    handleDatePickerChange,
    handleRawInputChange,
    handleDateBlur,
    headingPosition,
    headingPlacedHorizontally,
    messageProps,
    closeMessage,
    ...baseDatasheetElementController,
  };
};